import { useEffect } from 'react'
import { upsertJsonLd, upsertLinkTag, upsertMetaTag } from './dom'
import { SITE_NAME, absoluteUrl } from './site'
import { useLocale } from '../../i18n/locale'

export type PageSeoOptions = {
  title: string
  description: string
  /** Ścieżka bez hosta, np. `/blog` */
  pathname: string
  keywords?: string
  ogType?: 'website' | 'article'
  /** np. dla 404 */
  noindex?: boolean
  /** Pełny URL (https://…) , obrazek Open Graph / Twitter */
  imageUrl?: string | null
  jsonLd?: unknown
  jsonLdScriptId?: string
}

const DEFAULT_JSONLD_ID = 'page-jsonld'

export function usePageSeo(opts: PageSeoOptions) {
  const jsonLdKey = opts.jsonLd === undefined ? '' : JSON.stringify(opts.jsonLd)
  const { ogLocale } = useLocale()

  useEffect(() => {
    const prevTitle = document.title
    const title = opts.title.includes(SITE_NAME) ? opts.title : `${opts.title} – ${SITE_NAME}`
    const canonicalUrl = absoluteUrl(opts.pathname)
    const image =
      opts.imageUrl?.trim() ||
      (import.meta.env.VITE_DEFAULT_OG_IMAGE as string | undefined)?.trim() ||
      null

    document.title = title

    const nodes: Array<HTMLElement | null> = []

    nodes.push(
      upsertMetaTag('meta[name="description"]', {
        name: 'description',
        content: opts.description,
      }),
    )

    if (opts.keywords?.trim()) {
      nodes.push(
        upsertMetaTag('meta[name="keywords"]', {
          name: 'keywords',
          content: opts.keywords.trim(),
        }),
      )
    }

    nodes.push(
      upsertMetaTag('meta[property="og:title"]', { property: 'og:title', content: title }),
      upsertMetaTag('meta[property="og:description"]', {
        property: 'og:description',
        content: opts.description,
      }),
      upsertMetaTag('meta[property="og:type"]', {
        property: 'og:type',
        content: opts.ogType ?? 'website',
      }),
      upsertMetaTag('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl }),
      upsertMetaTag('meta[property="og:site_name"]', {
        property: 'og:site_name',
        content: SITE_NAME,
      }),
      upsertMetaTag('meta[property="og:locale"]', {
        property: 'og:locale',
        content: ogLocale,
      }),
    )

    if (image) {
      nodes.push(
        upsertMetaTag('meta[property="og:image"]', { property: 'og:image', content: image }),
      )
    }

    nodes.push(
      upsertMetaTag('meta[name="twitter:card"]', {
        name: 'twitter:card',
        content: image ? 'summary_large_image' : 'summary',
      }),
      upsertMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: title }),
      upsertMetaTag('meta[name="twitter:description"]', {
        name: 'twitter:description',
        content: opts.description,
      }),
    )

    if (image) {
      nodes.push(
        upsertMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: image }),
      )
    }

    if (opts.noindex) {
      nodes.push(
        upsertMetaTag('meta[name="robots"]', {
          name: 'robots',
          content: 'noindex, follow',
        }),
      )
    }

    nodes.push(upsertLinkTag('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl }))

    let jsonNode: HTMLScriptElement | null = null
    if (opts.jsonLd !== undefined) {
      const id = opts.jsonLdScriptId ?? DEFAULT_JSONLD_ID
      jsonNode = upsertJsonLd(id, opts.jsonLd)
    }

    return () => {
      document.title = prevTitle
      for (const el of nodes) {
        el?.remove()
      }
      jsonNode?.remove()
    }
  }, [
    opts.title,
    opts.description,
    opts.pathname,
    opts.keywords,
    opts.ogType,
    opts.noindex,
    opts.imageUrl,
    jsonLdKey,
    opts.jsonLdScriptId,
    ogLocale,
  ])
}
